import { ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { TopNavigation } from "./TopNavigation";
import { PeriodFilter } from "./PeriodFilter";
import { DateRangeFilter } from "./DateRangeFilter";
import { LogoutButton } from "./LogoutButton";
import { usePeriod } from "@/contexts/PeriodContext";
import { useRoutePersistence } from "@/hooks/useRoutePersistence";
import { Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  
  // Persist and restore last visited route
  useRoutePersistence();

  const { 
    selectedYear,
    selectedMonth,
    setYear,
    setMonth,
    reportsYear,
    reportsFromMonth,
    reportsToMonth,
    setReportsYear,
    setReportsFromMonth,
    setReportsToMonth,
    activeReportTab,
    taxReturnFY,
    setTaxReturnFY,
  } = usePeriod();
  
  const isSalesPage = location.pathname === '/sales';
  const isExpenditurePage = location.pathname === '/expenditure';
  const isBillsPage = location.pathname === '/bills';
  const isAccountsPage = location.pathname === '/' || location.pathname === '/accounts';
  const isReportsPage = location.pathname === '/reports';
  const isProfitDistributionPage = location.pathname === '/profit-distribution';
  const isWagesPage = location.pathname === '/wages';
  const isSettingsPage = location.pathname === '/settings';
  const isCashFlowPage = location.pathname === '/cash-flow';
  const isPayablesPage = location.pathname === '/payables';
  const isReceivablesPage = location.pathname === '/receivables';
  const isTrialBalancePage = location.pathname === '/trial-balance';

  // Available financial years for Tax Return (last 5 years)
  const availableFYs = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const fyStartYear = month >= 7 ? year : year - 1;
    const years = [];
    for (let i = 0; i < 5; i++) {
      const fy = fyStartYear - i;
      years.push(`${fy}-${fy + 1}`);
    }
    return years;
  }, []);

  const showPeriodFilter = isAccountsPage || isSalesPage || isExpenditurePage || isBillsPage || isProfitDistributionPage || isWagesPage || isCashFlowPage || isPayablesPage || isReceivablesPage || isTrialBalancePage;
  const showReportsDateRangeFilter = isReportsPage && activeReportTab !== 'tax-return';
  const showReportsFYFilter = isReportsPage && activeReportTab === 'tax-return';

  return (
    <div className="h-screen bg-background flex flex-col w-full overflow-hidden">
      <header className="flex-shrink-0 border-b bg-card">
        <div className="px-4 py-3 md:px-6 md:py-4">
          {/* Row 1: Logo + Logout */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-primary rounded-lg">
                <Building2 className="h-5 w-5 md:h-6 md:w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-lg md:text-2xl font-bold">Garage Renaissance</h1>
                <p className="text-xs md:text-sm text-muted-foreground hidden sm:block">Financial Manager</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              {/* Desktop: Show filters inline */}
              <div className="hidden md:flex items-center gap-4">
                {showPeriodFilter && (
                  <PeriodFilter
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    onYearChange={setYear}
                    onMonthChange={setMonth}
                  />
                )}
                {showReportsDateRangeFilter && (
                  <DateRangeFilter
                    year={reportsYear}
                    fromMonth={reportsFromMonth}
                    toMonth={reportsToMonth}
                    onYearChange={setReportsYear}
                    onFromMonthChange={setReportsFromMonth}
                    onToMonthChange={setReportsToMonth}
                  />
                )}
                {showReportsFYFilter && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Financial Year:</span>
                    <Select value={taxReturnFY} onValueChange={setTaxReturnFY}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFYs.map(fy => (
                          <SelectItem key={fy} value={fy}>
                            FY {fy}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <LogoutButton />
            </div>
          </div>
          
          {/* Row 2: Period Filter (mobile only) */}
          <div className="md:hidden mt-2">
            {showPeriodFilter && (
              <PeriodFilter
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                onYearChange={setYear}
                onMonthChange={setMonth}
              />
            )}
            {showReportsDateRangeFilter && (
              <DateRangeFilter
                year={reportsYear}
                fromMonth={reportsFromMonth}
                toMonth={reportsToMonth}
                onYearChange={setReportsYear}
                onFromMonthChange={setReportsFromMonth}
                onToMonthChange={setReportsToMonth}
              />
            )}
            {showReportsFYFilter && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">FY:</span>
                <Select value={taxReturnFY} onValueChange={setTaxReturnFY}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFYs.map(fy => (
                      <SelectItem key={fy} value={fy}>
                        FY {fy}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </header>
      <TopNavigation />
      <main className="flex-1 overflow-auto p-3 md:p-6">
        {children}
      </main>
    </div>
  );
};